import { ActionTree } from "vuex";
import { StateInterface } from "src/store";
import MaObjects from "src/modules/ma/classes/ma_objects";
import MaRefs from "src/modules/ma/classes/ma_refs";
import API from "src/services/api/api";
import { IdomStateInterface } from "./state";
import {
  IdomMessagingPolicy,
  IdomSettings,
  IdomEntityItem,
  IdomBotItem,
  IdomAutosendOption,
} from "../../models";
interface IBotQueryParams {
  search: string;
  platform_guid: string | null;
  role_guid: string | null;
  blocked: boolean | null;
  limit: number | undefined;
  offset: number | undefined;
}

interface IChannelQueryParams {
  search: string;
  platform_guid: string | null;
  autosend: string | null;
  limit: number | undefined;
  offset: number | undefined;
}

interface IEntityQueryParams {
  search: string;
  limit: number | undefined;
  offset: number | undefined;
}

const actions: ActionTree<IdomStateInterface, StateInterface> = {
  async init(context) {
    // const isAdmin = context.rootGetters["user/checkAccess"](
    //   MaRefs.str.access.idomAdmin
    // );
    const tlgrmSet = (window as any).$store.state.settings.list.services.value
      .tlgrm;
    await context.dispatch("initSettings", tlgrmSet.settings);
    await context.dispatch("initPolicies", tlgrmSet.policies);
    await context.dispatch("initUserMap", tlgrmSet.user_tags);
  },
  initUserMap(context, map: string[]) {
    context.commit("setIdomUserMap", map);
  },

  async fetchBots(context, params: IBotQueryParams) {
    const bots = await API.sqlr.idom_get_accounts(params);
    const botsCount = await API.sqlr.idom_get_accounts_count({
      ...params,
      offset: undefined,
      limit: undefined,
    });
    await context.dispatch("initBots", bots);
    context.commit("setBotsCount", botsCount);
  },

  async fetchChannels(context, params: IChannelQueryParams) {
    const channels = await API.sqlr.idom_get_channels(params);
    const channelsCount = await API.sqlr.idom_get_channels_count({
      ...params,
      offset: undefined,
      limit: undefined,
    });
    context.commit("setIdomChannels", channels);
    context.commit("setIdomChannelCount", channelsCount);
  },

  async fetchEntities(context, params: IEntityQueryParams) {
    const entities = await API.sqlr.idom_get_entities(params);
    const entitiesCount = await API.sqlr.idom_get_entities_count({
      ...params,
      offset: undefined,
      limit: undefined,
    });
    context.commit("setTlgrmEntities", entities);
    context.commit("setTlgrmEntitiesCount", entitiesCount);
  },

  initBots(context, bots: IdomBotItem[]) {
    context.commit(
      "setIdomBots",
      bots
        .sort((a, b) => +(b.enabled && a.enabled))
        .map((v, i) => {
          v.num = i + 1;
          return v;
        }),
    );
  },
  initSettings(context, settings: IdomSettings) {
    context.commit("setIdomSettings", settings);
  },
  initPolicies(context, payload: IdomMessagingPolicy[]) {
    context.commit("setTlgrmPolicies", payload);
  },
  async initPlatforms(context) {
    if (!context.getters.getPlatforms.length) {
      const platforms = await API.sqlr.idom_get_platforms();
      context.commit("setPlatforms", platforms);
    }
  },
  async initAutosendOptions(context) {
    if (!context.getters.getAutosendOptions.length) {
      const autosendOptions = await API.sqlr.idom_get_autosend_options();
      context.commit("setAutosendOptions", autosendOptions);
    }
  },
  async initRoles(context) {
    if (!context.getters.getRoles.length) {
      const roles = await API.sqlr.idom_get_roles();
      context.commit("setRoles", roles);
    }
  },
  async swapSendingState(context, { settings_ref }) {
    const settingsRef = MaObjects.toRef(settings_ref);
    const typeRef = MaObjects.toRef(MaRefs.str.types.tlgrm_settings);
    const newValue = !context.state.settings.premoderate_sending_enabled;

    const x = await MaObjects.save(
      { premoderate_sending_enabled: newValue },
      settingsRef,
      typeRef,
    );
    const newSettings = x.items;
    newSettings.ref = MaObjects.ref2str(settingsRef);
    context.commit("setIdomSettings", newSettings);
  },
  async changeTlgrmSettingMessagingPolicy(
    context,
    { settings_ref, new_value },
  ) {
    const newPolicyRef = MaObjects.toRef(new_value);
    const settingsRef = MaObjects.toRef(settings_ref);
    const typeRef = MaObjects.toRef(MaRefs.str.types.tlgrm_settings);

    const x = await MaObjects.save(
      { policy: newPolicyRef },
      settingsRef,
      typeRef,
    );
    const v = await MaObjects.getObject(newPolicyRef);
    const newSettings = x.items;
    newSettings.policy = v.items;
    newSettings.policy.ref = MaObjects.ref2str(newPolicyRef);
    newSettings.ref = MaObjects.ref2str(settingsRef);
    context.commit("setIdomSettings", newSettings);
  },
  changeBotEnabledState(context, { row, value }) {
    const payload = {
      guid: row.account_guid,
      person_guid: row.person_guid,
      platform_guid: row.platform_guid,
      credentials: row.credentials,
      blocked: row.blocked,
      enabled: value,
    };
    const newRow = Object.assign({}, row);
    newRow.enabled = value;
    API.sqlr
      .idom_save_accounts([payload])
      .then(() => {
        context.commit("setBotState", {
          account_guid: newRow.account_guid,
          enabled: newRow.enabled,
        });
      })
      .catch((error: any) => {
        console.error(
          `IdomMain::channels.changeBotEnabledState.idom_save_accounts error:\n${error}`,
        );
      });
  },
  changeBotCredentialsState(context, { row, credentials }) {
    const payload = {
      guid: row.account_guid,
      person_guid: row.person_guid,
      platform_guid: row.platform_guid,
      credentials,
      blocked: row.blocked,
      enabled: row.enabled,
    };
    const newRow = Object.assign({}, row);
    newRow.credentials = credentials;
    API.sqlr
      .idom_save_accounts([payload])
      .then(() => {
        context.commit("setBotCredentials", {
          account_guid: newRow.account_guid,
          credentials: newRow.credentials,
        });
      })
      .catch((error: any) => {
        console.error(
          `IdomMain::channels.changeBotCredentialsState.idom_save_accounts error:\n${error}`,
        );
      });
  },
  changeChannelEnabledState(context, { row, value }) {
    const newRow = Object.assign({}, row);
    newRow.enabled = value;
    delete newRow.num;
    API.sqlr
      .idom_save_channels([newRow])
      .then(() => {
        context.commit("setChannelState", {
          guid: newRow.guid,
          enabled: newRow.enabled,
          autosend_option_guid: newRow.autosend_option_guid,
        });
      })
      .catch((error: any) => {
        console.error(
          `IdomMain::channels.changeChannelEnabledState.idom_save_channels error:\n${error}`,
        );
      });
  },
  changeChannelAutosendState(context, { row, value }) {
    const newRow = Object.assign({}, row);
    newRow.autosend_option_guid = value;
    delete newRow.num;
    API.sqlr
      .idom_save_channels([newRow])
      .then(() => {
        context.commit("setChannelState", {
          guid: newRow.guid,
          enabled: newRow.enabled,
          autosend_option_guid: newRow.autosend_option_guid,
        });
      })
      .catch((error: any) => {
        console.error(
          `IdomMain::channels.changeChannelAutosendState.idom_save_channels error:\n${error}`,
        );
      });
  },
  changeEntityState(context, { row, enabled, autosend, push_mode }) {
    const newRow = Object.assign({}, row);
    newRow.enabled = enabled;
    newRow.autosend = autosend;
    newRow.push_mode = push_mode;
    delete newRow.num;
    API.sqlr
      .idom_save_entities(newRow)
      .then(() => {
        context.commit("setEntityState", {
          guid: newRow.guid,
          enabled,
          autosend,
          push_mode,
        });
      })
      .catch((error) => {
        console.error(
          `IdomMain::channels.changeEntityState.idom_save_entities error:\n${error}`,
        );
      });
  },
};

export default actions;
